import React, { useEffect } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { TertiarySquareButton } from '@entur/button'
import { space } from '@entur/tokens'
import { LeftArrowIcon, RightArrowIcon } from '@entur/icons'
import { usePageableApi } from '../../api-clients/client-hooks'
import { getTicketRightHolders } from '../../api-clients/eba-clients'
import { FetchState, Page, TicketRightHolder } from '../../api-clients/api-types'
import { HistoryState } from '../../types'
import { useStateWithSessionStorage } from '../../hooks/common-hooks'

const findEmployeeId = (trh: TicketRightHolder) => {
  const t = trh.id.split('-')
  return t[0] === 'Employee' ? t[1] : t[2]
}

const findIds = (searchResult: Page<TicketRightHolder>) => {
  return searchResult?.items?.map(findEmployeeId) || []
}

type EmployeeSearchNavigationProps = {
  disabled?: boolean
}
const EmployeeSearchNavigation: React.FC<EmployeeSearchNavigationProps> = ({ disabled }) => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const loc = useLocation()
  const locState = loc.state as HistoryState | undefined

  const [previousPageResponse, doFetchPreviousPage] = usePageableApi(getTicketRightHolders)
  const [nextPageResponse, doFetchNextPage] = usePageableApi(getTicketRightHolders)

  // To get back to correct searchResult
  const [savedQuery, saveQuery] = useStateWithSessionStorage<string>('search-query-state', '')
  const [currentPage, setCurrentPage] = useStateWithSessionStorage('currentPage', 1)

  const page = locState?.search?.page || 0
  const searchResult = locState?.search?.searchResult
  const query = locState?.search?.query || ''
  const perPage = locState?.search?.perPage
  const totalPages = locState?.search?.searchResult?.totalPages

  const ids = searchResult ? findIds(searchResult) : []
  const resultIndex = locState?.newIndex || ids.indexOf(id || '')
  const isFirstPage = page === 1
  const isLastPage = page === totalPages
  const isLastElementOnPage = ids.length > 0 && resultIndex === ids.length - 1
  const isFirstElementOnPage = resultIndex === 0

  useEffect(() => {
    if (query !== savedQuery) {
      saveQuery(query)
    }
    if (page !== currentPage) {
      setCurrentPage(page)
    }
    if (
      query !== '' &&
      isFirstElementOnPage &&
      !isFirstPage &&
      previousPageResponse.status === FetchState.NOT_STARTED
    ) {
      doFetchPreviousPage({
        query: `${query}&page=${page - 1}&perPage=${perPage}`,
      })
    }
    if (query !== '' && isLastElementOnPage && !isLastPage && nextPageResponse.status === FetchState.NOT_STARTED) {
      doFetchNextPage({
        query: `${query}&page=${page + 1}&perPage=${perPage}`,
      })
    }
  })

  const navigateToNewEmployee = (
    newIndex: number,
    newId?: string,
    newPage: number = page,
    newSearchResult: Page<TicketRightHolder> | undefined = searchResult
  ) => {
    const isEmployee = newSearchResult === undefined || newSearchResult?.items[newIndex].isEmployee
    navigate(`/ansatt/${newId}${isEmployee ? '' : '#familiemedlemmer'}`, {
      state: {
        ...locState,
        newIndex,
        search: {
          ...locState?.search,
          page: newPage,
          searchResult: newSearchResult,
        },
      },
    })
  }

  const prevEmployee = () => {
    if (isFirstElementOnPage && previousPageResponse.status === FetchState.SUCCESS) {
      navigateToNewEmployee(
        previousPageResponse.data.items.length - 1,
        findIds(previousPageResponse.data).pop(),
        page - 1,
        previousPageResponse.data
      )
    } else {
      navigateToNewEmployee(resultIndex - 1, ids[resultIndex - 1])
    }
  }

  const forwardEmployee = () => {
    if (isLastElementOnPage && nextPageResponse.status === FetchState.SUCCESS) {
      navigateToNewEmployee(0, findIds(nextPageResponse.data).shift(), page + 1, nextPageResponse.data)
    } else {
      navigateToNewEmployee(resultIndex + 1, ids[resultIndex + 1])
    }
  }

  return searchResult ? (
    <>
      <TertiarySquareButton
        name="prevEmployee"
        onClick={prevEmployee}
        aria-label="Gå til forrige profil"
        style={{ marginLeft: space.medium }}
        disabled={
          disabled ||
          (isFirstPage && isFirstElementOnPage) ||
          (isFirstElementOnPage && previousPageResponse.status !== FetchState.SUCCESS)
        }
      >
        <LeftArrowIcon />
      </TertiarySquareButton>
      <TertiarySquareButton
        name="nextEmployee"
        onClick={forwardEmployee}
        aria-label="Gå til neste profil"
        style={{ marginLeft: space.extraSmall2 }}
        disabled={
          disabled ||
          (isLastPage && isLastElementOnPage) ||
          (isLastElementOnPage && nextPageResponse.status !== FetchState.SUCCESS)
        }
      >
        <RightArrowIcon />
      </TertiarySquareButton>
    </>
  ) : (
    <></>
  )
}

export default EmployeeSearchNavigation
